/**
 * LISTCTR-2 · 上架中心「手机站」标签 = 手机站预览台。
 * 契约：designs/mockups/channel-center/上架中心-样稿.html 第 2 版「手机站」标签（tag site-admin-mockups-v1）。
 *
 * 左：手机框 iframe 装**真手机站页面**（同一套 SSR，带预览令牌的预览模式：顶上一直挂「预览模式」，点提交 / 付款只演示、不下单不占座不发通知）
 *     + iPhone / 安卓尺寸 + 刷新 +「扫码用自己手机看」（二维码 = 带 2 小时预览令牌的地址）
 * 右：首页横幅（最多 5 张、拖动排序、选跳转线路、上线 / 下线、线路下架标红）/ 首页精选（拖动排序、只能选在展示的线路）/ 线路展示清单（原因 +「去补」）
 *     保存后左边自动刷新。
 * 接口：GET/PUT /api/v1/site/banners · /site/featured · GET /site/products-status · POST /site/preview-token
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { QRCodeSVG } from "qrcode.react";
import { ArrowDown, ArrowUp, GripVertical, ImageUp, Plus, RefreshCw, X } from "lucide-react";
import { Can } from "@/auth/permissions";
import { FixEnglishLink } from "@/components/MissingEnglish";
import {
  SITE_QK, createSitePreviewToken, getSiteBanners, getSiteFeatured, getSiteProductsStatus, putSiteBanners, putSiteFeatured,
  type SiteBanner, type SiteFeaturedItem, type SiteProductStatus,
} from "@/services/site";
import { uploadReceipt } from "@/services/uploads";
import { useReorder } from "./useReorder";
import { MobileDisplayDrawer } from "./MobileDisplayDrawer";
import { CategoriesTab } from "./CategoriesTab";   // SITECAT-1

type RTab = "banners" | "categories" | "featured" | "status";
type Device = "iphone" | "android";
const DEVICES: Record<Device, { label: string; w: number; h: number }> = {
  iphone: { label: "iPhone 尺寸", w: 390, h: 800 },
  android: { label: "安卓尺寸", w: 412, h: 840 },
};
const TOKEN_REFRESH_MS = 100 * 60 * 1000;   // 令牌 2 小时有效，100 分钟换一次

function Pill({ tone, children }: { tone: "green" | "amber" | "gray" | "red"; children: React.ReactNode }) {
  const cls = { green: "bg-emerald-50 text-emerald-600", amber: "bg-amber-50 text-amber-600", gray: "bg-slate-100 text-slate-600", red: "bg-rose-50 text-rose-600" }[tone];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[12px] font-medium ${cls}`}>
      <span className="h-[7px] w-[7px] rounded-full bg-current" />{children}
    </span>
  );
}

// ───────────────────────────── 左：手机框预览 ─────────────────────────────

function PreviewPane({ reloadKey, onReload }: { reloadKey: number; onReload: () => void }) {
  const [device, setDevice] = useState<Device>("iphone");
  const [qrOpen, setQrOpen] = useState(false);
  const tokenQ = useQuery({ queryKey: SITE_QK.token, queryFn: createSitePreviewToken, refetchInterval: TOKEN_REFRESH_MS, staleTime: TOKEN_REFRESH_MS });
  const path = tokenQ.data?.path;
  const dev = DEVICES[device];
  const absUrl = path ? `${window.location.origin}${path}` : "";

  return (
    <div className="flex flex-col items-center gap-2.5" data-testid="mobile-preview">
      <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-[12px] text-amber-800">预览模式 · 不下单 · 不占座 · 不发通知</span>
      <div className="flex flex-wrap items-center justify-center gap-1.5">
        {(Object.keys(DEVICES) as Device[]).map((k) => (
          <button key={k} type="button" onClick={() => setDevice(k)}
            className={`rounded-full border px-3.5 py-1 text-[13px] ${device === k ? "border-blue-600 bg-blue-600 font-semibold text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
            {DEVICES[k].label}
          </button>
        ))}
        <button type="button" onClick={() => { tokenQ.refetch(); onReload(); }}
          className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3.5 py-1 text-[13px] text-slate-600 hover:bg-slate-50"><RefreshCw size={12} />刷新</button>
        <button type="button" disabled={!absUrl} onClick={() => setQrOpen(true)}
          className="rounded-full border border-slate-200 bg-white px-3.5 py-1 text-[13px] text-slate-600 hover:bg-slate-50 disabled:opacity-50">▣ 扫码用自己手机看</button>
      </div>
      {tokenQ.isError ? (
        <button type="button" onClick={() => tokenQ.refetch()} className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-6 text-[13px] font-medium text-rose-700">预览加载失败，点击重试</button>
      ) : !path ? (
        <div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white text-[13px] text-slate-400" style={{ width: dev.w, height: dev.h, maxWidth: "100%" }}>加载中…</div>
      ) : (
        <iframe key={`${reloadKey}-${tokenQ.dataUpdatedAt}-${device}`} src={path} title="手机站预览" data-testid="mobile-preview-frame"
          className="rounded-2xl border-[8px] border-slate-800 bg-white" style={{ width: dev.w + 16, height: dev.h + 16, maxWidth: "100%" }} />
      )}
      <p className="text-center text-[12px] text-slate-400">这里面就是真的手机站（同一套页面），能点能滑；点提交 / 付款只演示流程，不会真的下单</p>

      {qrOpen && absUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm animate-in fade-in" role="dialog" aria-label="扫码用自己手机看">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl animate-in zoom-in-95 slide-in-from-bottom-4">
            <div className="mb-3 flex items-center justify-between"><h3 className="text-base font-semibold text-slate-900">扫码用自己手机看</h3>
              <button type="button" onClick={() => setQrOpen(false)} aria-label="关闭" className="rounded p-1 text-slate-400 hover:bg-slate-100"><X size={16} /></button></div>
            <div className="flex justify-center rounded-lg border border-slate-200 p-3"><QRCodeSVG value={absUrl} size={200} /></div>
            <p className="mt-3 text-[12px] leading-relaxed text-slate-500">用手机相机或微信扫一扫。链接带 2 小时有效的预览令牌，过期后回到这里重新打开；预览里点提交 / 付款只演示，不会真的下单。别把这个链接发给客人。</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ───────────────────────────── 右 · 首页横幅 ─────────────────────────────

// 横幅图走 POST /api/v1/uploads（kind=public，手机站匿名可读），不再手填图片地址
const BANNER_TYPES = ["image/jpeg", "image/png", "image/webp"];
const BANNER_ACCEPT = BANNER_TYPES.join(",");

function BannersTab({ statusItems, onSaved }: { statusItems: SiteProductStatus[]; onSaved: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: SITE_QK.banners, queryFn: getSiteBanners });
  const [draft, setDraft] = useState<SiteBanner[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [newPid, setNewPid] = useState("");
  const [err, setErr] = useState<string | null>(null);
  // 点击上传（替代手填图片地址）：busy = 正在传哪一张（"new" = 新增那张）；note = 尺寸偏小之类的提示（只提示不拦）
  const [busy, setBusy] = useState<number | "new" | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const upload = async (file: File | undefined, slot: number | "new", done: (url: string) => void) => {
    if (!file) return;
    if (!BANNER_TYPES.includes(file.type)) { setErr("只支持 jpg / png / webp 图片"); return; }
    setBusy(slot); setErr(null); setNote(null);
    try {
      const r = (await uploadReceipt(file, file.name, "public")) as { url: string; width?: number | null };
      done(r.url);
      if (r.width && r.width < 1200) setNote(`这张图宽 ${r.width} px，小于建议的 1200 px，手机上可能有点糊（可以照用）`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "上传失败，请重试");
    } finally {
      setBusy(null);
    }
  };
  const max = q.data?.max ?? 5;
  const items = draft ?? q.data?.items ?? [];
  const dirty = draft !== null;
  const { move, dragProps } = useReorder(items, (v) => setDraft(v));
  const showingProducts = statusItems.filter((s) => s.showing);
  const titleOf = (id: string) => statusItems.find((s) => s.product_id === id)?.title ?? items.find((b) => b.product_id === id)?.product_title ?? "（线路）";
  const saveMut = useMutation({
    mutationFn: () => putSiteBanners(items.map((b) => ({ image_url: b.image_url, product_id: b.product_id, active: b.active }))),
    onSuccess: (d) => { qc.setQueryData(SITE_QK.banners, d); setDraft(null); setErr(null); onSaved(); },
    onError: (e) => setErr(e instanceof Error ? e.message : "保存失败，请重试"),
  });
  const edit = (i: number, patch: Partial<SiteBanner>) => setDraft(items.map((b, k) => (k === i ? { ...b, ...patch } : b)));

  if (q.isLoading) return <p className="text-sm text-slate-400">加载中…</p>;
  if (q.isError) return <button type="button" onClick={() => q.refetch()} className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">加载失败，点击重试</button>;

  return (
    <div className="flex flex-col gap-3" data-testid="banners-tab">
      <p className="text-[12.5px] text-slate-500">最多 {max} 张 · 拖动排顺序 · 每张选点了跳到哪条在售线路 · 改完点「保存」左边马上刷新</p>
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-3">
        {items.map((b, i) => {
          const stale = b.showing === false && b.active && !dirty;
          const gone = statusItems.length > 0 && !statusItems.some((s) => s.product_id === b.product_id && s.showing);
          return (
            <div key={`${b.id ?? "new"}-${i}`} {...dragProps(i)} data-testid="banner-card"
              className={`flex flex-col overflow-hidden rounded-[10px] border bg-white text-[12px] ${gone || stale ? "border-rose-300" : "border-slate-200"}`}>
              <div className="group relative aspect-[3/2] bg-slate-800 bg-cover bg-top" style={{ backgroundImage: `url('${b.image_url.replace(/'/g, "%27")}')` }}>
                <span className="absolute left-1 top-1 rounded bg-slate-900/70 px-1.5 text-[11px] text-white">{i + 1}</span>
                <Can perm="listing.mappings.manage">
                  <label className={`absolute inset-0 flex cursor-pointer items-center justify-center bg-slate-900/50 text-[12px] font-medium text-white transition-opacity ${busy === i ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}>
                    {busy === i ? "上传中…" : <span className="inline-flex items-center gap-1"><ImageUp size={14} />点击换图</span>}
                    <input type="file" accept={BANNER_ACCEPT} className="hidden" disabled={busy !== null} aria-label="换横幅图片"
                      onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; void upload(f, i, (url) => edit(i, { image_url: url })); }} />
                  </label>
                </Can>
              </div>
              <div className="flex flex-col gap-1.5 p-2 text-slate-600">
                <div className="flex items-center gap-1 text-slate-400">
                  <GripVertical size={13} className="cursor-grab" /><span>拖动</span>
                  <Can perm="listing.mappings.manage">
                    <span className="ml-auto flex gap-0.5">
                      <button type="button" aria-label="上移" onClick={() => move(i, i - 1)} className="rounded p-0.5 hover:bg-slate-100"><ArrowUp size={12} /></button>
                      <button type="button" aria-label="下移" onClick={() => move(i, i + 1)} className="rounded p-0.5 hover:bg-slate-100"><ArrowDown size={12} /></button>
                      <button type="button" aria-label="移除横幅" onClick={() => setDraft(items.filter((_, k) => k !== i))} className="rounded p-0.5 hover:bg-rose-50 hover:text-rose-600"><X size={12} /></button>
                    </span>
                  </Can>
                </div>
                <label className="flex flex-col gap-0.5">跳到：
                  <select aria-label="跳转线路" value={b.product_id} onChange={(e) => edit(i, { product_id: e.target.value })}
                    className="rounded border border-slate-300 px-1.5 py-1 text-[12px] focus:outline-none focus:ring-1 focus:ring-blue-500">
                    {!showingProducts.some((s) => s.product_id === b.product_id) && <option value={b.product_id}>{titleOf(b.product_id)}（没在展示）</option>}
                    {showingProducts.map((s) => <option key={s.product_id} value={s.product_id}>{s.title}</option>)}
                  </select>
                </label>
                <button type="button" onClick={() => edit(i, { active: !b.active })} className="self-start" aria-pressed={b.active}>
                  {gone ? <Pill tone="red">线路已下架 · 自动不显示</Pill> : b.active ? <Pill tone="green">上线</Pill> : <Pill tone="gray">下线</Pill>}
                </button>
              </div>
            </div>
          );
        })}
        {items.length < max && (
          <Can perm="listing.mappings.manage">
            <button type="button" onClick={() => setAdding(true)} data-testid="banner-add"
              className="flex min-h-[150px] flex-col items-center justify-center rounded-[10px] border border-dashed border-slate-300 text-slate-400 hover:border-blue-400 hover:text-blue-600">
              <Plus size={16} />添加横幅<span className="text-[11px]">建议 3:2 · 宽 ≥ 1200 px</span>
            </button>
          </Can>
        )}
      </div>
      {adding && (
        <div className="flex flex-col gap-2 rounded-lg border border-blue-200 bg-blue-50/40 p-3 text-[12.5px]">
          <span>横幅图片</span>
          <label data-testid="banner-upload"
            className={`relative flex aspect-[3/2] w-full max-w-[360px] cursor-pointer flex-col items-center justify-center gap-1 overflow-hidden rounded-[10px] border border-dashed bg-white bg-cover bg-top text-slate-400 hover:border-blue-400 hover:text-blue-600 ${newUrl ? "border-slate-200" : "border-slate-300"}`}
            style={newUrl ? { backgroundImage: `url('${newUrl.replace(/'/g, "%27")}')` } : undefined}>
            {busy === "new" ? (
              <span className="rounded bg-white/90 px-2 py-0.5 text-[12px] text-slate-600">上传中…</span>
            ) : newUrl ? (
              <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded bg-slate-900/70 px-2 py-0.5 text-[11.5px] text-white"><ImageUp size={12} />换一张</span>
            ) : (
              <><ImageUp size={20} /><span className="text-[13px]">点击上传横幅图片</span><span className="text-[11px]">jpg / png / webp · 建议 3:2 · 宽 ≥ 1200 px</span></>
            )}
            <input type="file" accept={BANNER_ACCEPT} className="hidden" disabled={busy !== null} aria-label="上传横幅图片"
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; void upload(f, "new", setNewUrl); }} />
          </label>
          <label className="flex flex-col gap-1">跳到哪条线路（只能选在手机站展示中的）
            <select value={newPid} onChange={(e) => setNewPid(e.target.value)} className="rounded border border-slate-300 px-2 py-1.5 text-sm">
              <option value="">请选择</option>{showingProducts.map((s) => <option key={s.product_id} value={s.product_id}>{s.title}（{s.code}）</option>)}
            </select>
          </label>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => { setAdding(false); setNewUrl(""); setNote(null); }} className="rounded-md border border-slate-300 bg-white px-3 py-1 text-[12.5px]">取消</button>
            <button type="button" disabled={!newUrl.trim() || !newPid || busy !== null} onClick={() => { setDraft([...items, { image_url: newUrl.trim(), product_id: newPid, active: true }]); setAdding(false); setNewUrl(""); setNewPid(""); }}
              className="rounded-md bg-blue-600 px-3 py-1 text-[12.5px] font-medium text-white disabled:opacity-50">加入</button>
          </div>
        </div>
      )}
      {note && <p className="text-xs text-amber-600">{note}</p>}
      {err && <p className="text-xs text-rose-600">{err}</p>}
      <Can perm="listing.mappings.manage">
        <div className="flex justify-end gap-2">
          <button type="button" disabled={!dirty} onClick={() => { setDraft(null); setErr(null); setNote(null); }} className="rounded-md border border-slate-300 bg-white px-3.5 py-1.5 text-[13px] disabled:opacity-50">取消</button>
          <button type="button" disabled={!dirty || saveMut.isPending} onClick={() => saveMut.mutate()} className="rounded-md bg-blue-600 px-3.5 py-1.5 text-[13px] font-medium text-white disabled:opacity-50">{saveMut.isPending ? "保存中…" : "保存"}</button>
        </div>
      </Can>
    </div>
  );
}

// ───────────────────────────── 右 · 首页精选 ─────────────────────────────

function FeaturedTab({ statusItems, onSaved }: { statusItems: SiteProductStatus[]; onSaved: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: SITE_QK.featured, queryFn: getSiteFeatured });
  const [draft, setDraft] = useState<SiteFeaturedItem[] | null>(null);
  const [pick, setPick] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const items = draft ?? q.data?.items ?? [];
  const dirty = draft !== null;
  const { move, dragProps } = useReorder(items, (v) => setDraft(v));
  const addable = statusItems.filter((s) => s.showing && !items.some((i) => i.product_id === s.product_id));
  const saveMut = useMutation({
    mutationFn: () => putSiteFeatured(items.map((i) => i.product_id)),
    onSuccess: (d) => { qc.setQueryData(SITE_QK.featured, d); setDraft(null); setErr(null); onSaved(); },
    onError: (e) => setErr(e instanceof Error ? e.message : "保存失败，请重试"),
  });
  if (q.isLoading) return <p className="text-sm text-slate-400">加载中…</p>;
  if (q.isError) return <button type="button" onClick={() => q.refetch()} className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">加载失败，点击重试</button>;
  return (
    <div className="flex flex-col gap-3" data-testid="featured-tab">
      <p className="text-[12.5px] text-slate-500">挑线路放首页「精选线路」，拖动排顺序；这个顺序也是「全部线路」按「推荐」排序时的前几位</p>
      <table className="w-full text-[13px]">
        <thead><tr className="border-b border-slate-200 text-left text-[12px] text-slate-500"><th className="w-[84px] py-2">顺序</th><th>线路</th><th>手机站</th><th /></tr></thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-slate-400">还没有精选线路（首页「精选线路」整块不显示）</td></tr>}
          {items.map((it, i) => (
            <tr key={it.product_id} {...dragProps(i)} className="border-b border-slate-100" data-testid="featured-row">
              <td className="py-2.5"><span className="inline-flex items-center gap-1 text-slate-400"><GripVertical size={14} className="cursor-grab" />{i + 1}</span>
                <Can perm="listing.mappings.manage">
                  <span className="ml-1 inline-flex gap-0.5">
                    <button type="button" aria-label="上移" onClick={() => move(i, i - 1)} className="rounded p-0.5 hover:bg-slate-100"><ArrowUp size={12} /></button>
                    <button type="button" aria-label="下移" onClick={() => move(i, i + 1)} className="rounded p-0.5 hover:bg-slate-100"><ArrowDown size={12} /></button>
                  </span>
                </Can></td>
              <td><b>{it.product_title}</b><div className="text-[11.5px] text-slate-400">{it.product_code}</div></td>
              <td>{it.showing ? <Pill tone="green">在展示</Pill> : <Pill tone="amber">没展示{it.reason_label ? `：${it.reason_label}` : ""}</Pill>}</td>
              <td className="text-right"><Can perm="listing.mappings.manage">
                <button type="button" onClick={() => setDraft(items.filter((_, k) => k !== i))} className="rounded-md border border-slate-300 px-2 py-0.5 text-[12px] text-slate-600 hover:bg-slate-50">移出</button></Can></td>
            </tr>
          ))}
          <Can perm="listing.mappings.manage">
            <tr><td colSpan={4} className="py-2.5">
              <span className="inline-flex items-center gap-2">
                <select aria-label="添加线路" value={pick} onChange={(e) => setPick(e.target.value)} className="rounded border border-slate-300 px-2 py-1 text-[12.5px]">
                  <option value="">＋ 添加线路…</option>{addable.map((s) => <option key={s.product_id} value={s.product_id}>{s.title}（{s.code}）</option>)}
                </select>
                <button type="button" disabled={!pick} onClick={() => {
                  const s = statusItems.find((x) => x.product_id === pick)!;
                  setDraft([...items, { product_id: s.product_id, product_code: s.code, product_title: s.title, showing: true, reason_label: null }]); setPick("");
                }} className="rounded-md border border-slate-300 px-2.5 py-1 text-[12.5px] disabled:opacity-50">添加</button>
                <span className="text-[11.5px] text-slate-400">只能选在手机站展示中的线路</span>
              </span></td></tr>
          </Can>
        </tbody>
      </table>
      {err && <p className="text-xs text-rose-600">{err}</p>}
      <Can perm="listing.mappings.manage">
        <div className="flex justify-end gap-2">
          <button type="button" disabled={!dirty} onClick={() => { setDraft(null); setErr(null); }} className="rounded-md border border-slate-300 bg-white px-3.5 py-1.5 text-[13px] disabled:opacity-50">取消</button>
          <button type="button" disabled={!dirty || saveMut.isPending} onClick={() => saveMut.mutate()} className="rounded-md bg-blue-600 px-3.5 py-1.5 text-[13px] font-medium text-white disabled:opacity-50">{saveMut.isPending ? "保存中…" : "保存"}</button>
        </div>
      </Can>
    </div>
  );
}

// ───────────────────────────── 右 · 线路展示清单 ─────────────────────────────

function StatusTab({ items, total, showing, focusProductId, onSaved }: { items: SiteProductStatus[]; total: number; showing: number; focusProductId?: string | null; onSaved: () => void }) {
  const [editing, setEditing] = useState<SiteProductStatus | null>(null);
  useEffect(() => { if (focusProductId) document.getElementById(`status-row-${focusProductId}`)?.scrollIntoView({ block: "center" }); }, [focusProductId, items.length]);
  return (
    <div className="flex flex-col gap-3" data-testid="status-tab">
      <p className="text-[12.5px] text-slate-500">所有在售线路在手机站上的情况（{showing} / {total} 条在展示）· 点「编辑展示」改这条线在手机站上的卡片文字 · 没展示的写原因，点「去补」跳到那条产品的「内容与上架」</p>
      <table className="w-full text-[13px]">
        <thead><tr className="border-b border-slate-200 text-left text-[12px] text-slate-500"><th className="py-2">线路</th><th>对客标题</th><th>手机站</th><th>原因</th><th /></tr></thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-slate-400">还没有在售线路</td></tr>}
          {items.map((s) => (
            <tr key={s.product_id} id={`status-row-${s.product_id}`} className={`border-b border-slate-100 ${focusProductId === s.product_id ? "bg-blue-50/60" : ""}`} data-testid="status-row">
              <td className="py-2.5"><b>{s.code}</b></td>
              <td data-testid="status-public-title">{s.public_title ? s.public_title : <span className="text-slate-400">（空 = 用产品名）</span>}</td>
              <td>
                {s.showing ? <Pill tone="green">在展示</Pill> : <Pill tone="amber">没展示</Pill>}
                {/* ENNAME-3 · 中文版在展示但缺英文名：英文版不出这条线 */}
                {s.en_label && (
                  <span className="ml-1.5 whitespace-nowrap text-[11.5px] text-slate-400" data-testid="status-en-hidden">
                    {s.en_label}<FixEnglishLink productId={s.product_id} />
                  </span>
                )}
              </td>
              <td className="text-amber-600">{s.showing ? "—" : s.reason_label}</td>
              <td className="whitespace-nowrap text-right">
                <Can perm="products.products.update">
                  <button type="button" data-testid="edit-display" onClick={() => setEditing(s)} className="mr-1.5 rounded-md border border-slate-300 px-2 py-0.5 text-[12px] text-slate-600 hover:bg-slate-50">编辑展示</button>
                </Can>
                {!s.showing && (
                  <Link to={`/products/${s.product_id}?tab=content`} className="rounded-md border border-slate-300 px-2 py-0.5 text-[12px] text-slate-600 hover:bg-slate-50">去补 ›</Link>)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {editing && <MobileDisplayDrawer productId={editing.product_id} code={editing.code} onClose={() => setEditing(null)} onSaved={onSaved} />}
    </div>
  );
}

// ───────────────────────────── 整体 ─────────────────────────────

export function MobileSiteConsole({ focusProductId }: { focusProductId?: string | null } = {}) {
  // PPR-3 · 从产品详情状态条点「手机站」/「去补」落到这里：直接开「线路展示清单」并定位到那一行
  const [tab, setTab] = useState<RTab>(focusProductId ? "status" : "banners");
  const [reloadKey, setReloadKey] = useState(0);
  const qc = useQueryClient();
  const statusQ = useQuery({ queryKey: SITE_QK.status, queryFn: getSiteProductsStatus });
  const items = useMemo(() => statusQ.data?.items ?? [], [statusQ.data]);
  // 保存后：左边预览刷新；展示清单也重拉（横幅 / 精选改了不影响，但产品状态可能在别处改了）
  const afterSave = () => { setReloadKey((k) => k + 1); qc.invalidateQueries({ queryKey: SITE_QK.status }); };
  useEffect(() => { /* 进入标签时拿一份最新清单 */ qc.invalidateQueries({ queryKey: SITE_QK.status }); }, [qc]);

  return (
    <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-[430px_minmax(0,1fr)]" data-testid="mobile-site-console">
      <PreviewPane reloadKey={reloadKey} onReload={() => setReloadKey((k) => k + 1)} />
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex gap-1 border-b border-slate-200">
          {([["banners", "首页横幅"], ["categories", "首页分类"], ["featured", "首页精选"], ["status", "线路展示清单"]] as const).map(([k, label]) => (
            <button key={k} type="button" data-testid={`site-rtab-${k}`} onClick={() => setTab(k)}
              className={`border-b-2 px-3.5 py-2 text-[13px] ${tab === k ? "border-blue-600 font-semibold text-blue-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}>{label}</button>
          ))}
        </div>
        {statusQ.isError ? (
          <button type="button" onClick={() => statusQ.refetch()} className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">展示清单加载失败，点击重试</button>
        ) : tab === "banners" ? <BannersTab statusItems={items} onSaved={afterSave} />
          : tab === "categories" ? <CategoriesTab statusItems={items} onSaved={afterSave} />
          : tab === "featured" ? <FeaturedTab statusItems={items} onSaved={afterSave} />
            : <StatusTab items={items} total={statusQ.data?.total ?? 0} showing={statusQ.data?.showing ?? 0} focusProductId={focusProductId} onSaved={afterSave} />}
      </div>
    </div>
  );
}
